import React, { useState, useRef } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import './ArticleList.scss';

import { link } from '../../helpers/link';
import { convert } from '../../helpers/date';
import sendEvent from '../../helpers/tracking';
import { getString } from '../../helpers/styles';
import { getAdvertisementData } from '../../helpers/metadata';
import { src, srcSet, useDimensions } from '../../helpers/image';

import RightArrow from '../../shared/assets/icons/rightArrow.svg';

const MOBILE_CHUNK = 5;
const DESKTOP_CHUNK = 30;
const DESKTOP_THRESHOLD = 20;

export default function ArticleList(props) {
  const {
    view,
    list,
    title,
    withTabs,
    category,
    fetchMore,
    isHomePage,
    withoutTitle,
    unlimitedList,
    setRecentContent,
    fetchMoreResultKey: key,
    fetchMoreResultInnerKey: innerKey
  } = props;

  const onlySidebar = view === 'sidebar';
  const withoutSidebar = view === 'withoutSidebar' || list.length <= 6;

  const dashboardSection = view === 'dashboardSection';

  const observer = {};

  const [mobileList, setMobileList] = useState(list.slice(0, MOBILE_CHUNK));

  const [observableNodes, setObservableNodes] = useState(new Set());

  if (list.length && !mobileList.length)
    setMobileList(list.slice(0, MOBILE_CHUNK));

  return onlySidebar ? (
    <div className="articleList_sidebar_view">
      <DesktopSidebar list={list} />
    </div>
  ) : (
    <section id="articleList">
      {!withoutTitle && <h2 className="articleList_desktop">{title}</h2>}

      {withTabs ? (
        <div className="articleList_mobile">
          <h2>Artikelen in {category}</h2>

          <ul id="articleList_tabs">
            <li
              className={getString(
                'articleList_tabs_active',
                innerKey === 'recentChildContent'
              )}
              onClick={() => {
                setRecentContent(true);

                setMobileList([]);
              }}
            >
              Meest recent
            </li>

            <li
              className={getString(
                'articleList_tabs_active',
                innerKey === 'popularChildContent'
              )}
              onClick={() => {
                setRecentContent(false);

                setMobileList([]);
              }}
            >
              Populair
            </li>
          </ul>
        </div>
      ) : withoutTitle ? null : (
        <h2 className="articleList_mobile">{title}</h2>
      )}

      <div
        id="articleList_content"
        className={getString([
          {
            value: 'articleList_content_unlimited',
            condition: unlimitedList
          },
          {
            value: 'articleList_content_without_sidebar',
            condition: withoutSidebar
          }
        ])}
      >
        <div id="articleList_main">
          <ul className="articleList_items">
            {(unlimitedList ? list : list.slice(0, withoutSidebar ? 6 : 4)).map(
              (item) => (
                <li className="articleList_item" key={item.id}>
                  <Article
                    item={item}
                    eventTrackingCodes={['home', 'magazine-center']}
                  />
                </li>
              )
            )}
          </ul>
        </div>

        {!withoutSidebar && <DesktopSidebar list={list.slice(4)} />}

        <MobileSidebar list={list} />
      </div>
    </section>
  );

  function DesktopSidebar({ list }) {
    return (
      <aside className="articleList_desktop articleList_sidebar">
        {!withoutTitle && <h4>{title}</h4>}

        <ul className="articleList_items">
          {list.map((item, idx) => (
            <li key={item.id} className="articleList_item">
              <Article
                item={item}
                eventTrackingCodes={['home', 'magazine-right']}
              />

              {idx === list.length - DESKTOP_THRESHOLD && (
                <span data-id={idx} />
              )}
            </li>
          ))}
        </ul>
      </aside>
    );
  }

  function MobileSidebar({ list }) {
    return (
      <aside className="articleList_mobile articleList_sidebar">
        {!withoutTitle && <h4>{title}</h4>}

        <ul className="articleList_items">
          {mobileList.map((item, idx) => {
            const isFirstItem = !dashboardSection && !withTabs && idx === 0;

            const currentItem = { ...item };

            currentItem.isFirstItem = isFirstItem;

            return (
              <li
                key={currentItem.id}
                className="articleList_item"
                id={getString('articleList_first_item', isFirstItem)}
              >
                <Article
                  item={currentItem}
                  eventTrackingCodes={['home', 'magazine-center']}
                />
              </li>
            );
          })}
        </ul>

        {!unlimitedList && (
          <div className="articleList_mobile" id="articleList_loadmore_button">
            <button
              onClick={() => {
                loadMobileChunk(MOBILE_CHUNK);

                if (isHomePage) sendEvent('home', 'lees-meer-home');
              }}
            >
              Lees meer
            </button>
          </div>
        )}
      </aside>
    );
  }

  function Article({ item, eventTrackingCodes }) {
    const { imageUrl, title, author, uri, parent, postedAt, isFirstItem } =
      item;

    const [adName, adUrl] = getAdvertisementData(item.metadata) || [];

    const postedDate = convert(postedAt.replace(/-/g, '/'), 'nl');

    const ref = useRef();

    const dimensions = useDimensions(ref);

    return (
      <React.Fragment>
        {link(
          <div
            ref={ref}
            className="articleList_item_image"
            onClick={() =>
              isHomePage && sendEvent(...eventTrackingCodes, 'image')
            }
          >
            {dimensions &&
              (isFirstItem && isHomePage ? (
                <img
                  src={imageUrl}
                  alt="article-image"
                  src={firstItemSrc(imageUrl)}
                />
              ) : (
                <LazyLoadImage
                  threshold={0}
                  alt="article-image"
                  visibleByDefault={true}
                  srcSet={srcSet(imageUrl, dimensions)}
                />
              ))}

            {parent.title && <p>{parent.title}</p>}
          </div>,
          `/${uri}`
        )}

        <div
          onClick={() => isHomePage && sendEvent(...eventTrackingCodes, 'text')}
          className="articleList_item_data"
        >
          {link(<h6>{title}</h6>, `/${uri}`)}

          {authorSection(author, adName, adUrl, uri)}

          <a href={`/${uri}`} className="articleList_link">
            <span>
              Lees verder <RightArrow />
            </span>
          </a>
        </div>

        {dashboardSection && (
          <p className="articleList_item_date">{postedDate}</p>
        )}
      </React.Fragment>
    );
  }

  function authorSection(author, adName, adUrl, uri) {
    const ref = useRef();

    const dimensions = useDimensions(ref);

    return (
      <div
        className={getString([
          {
            value: 'articleList_item_author',
            condition: true
          },
          {
            value: 'articleList_item_advertisement',
            condition: adName
          }
        ])}
      >
        {author && (
          <React.Fragment>
            <div ref={ref} className="articleList_item_author_image">
              {dimensions && (
                <LazyLoadImage
                  width="30"
                  height="30"
                  threshold={0}
                  alt="author-image"
                  visibleByDefault={true}
                  srcSet={srcSet(author.imageUrl, dimensions)}
                />
              )}
            </div>

            {adName ? (
              <p>
                In samenwerking met:{' '}
                <a href={adUrl} target="_blank" rel="noopener noreferrer">
                  {adName}
                </a>
              </p>
            ) : (
              <a href={`/${uri}`}>
                <p>{author.displayName}</p>
              </a>
            )}
          </React.Fragment>
        )}
      </div>
    );
  }

  function observe(node) {
    if (!node) return;

    if (!observer.instanse)
      observer.instanse = new IntersectionObserver((entries) =>
        entries.forEach(
          (entry) => entry.isIntersecting && fetchMoreArticles(DESKTOP_CHUNK)
        )
      );

    const { id } = node.dataset;

    if (observableNodes.has(id)) return;

    setObservableNodes(observableNodes.add(id));

    observer.instanse.observe(node);
  }

  function loadMobileChunk() {
    if (mobileList.length < list.length)
      setMobileList(list.slice(0, mobileList.length + MOBILE_CHUNK));
    else fetchMoreArticles(MOBILE_CHUNK, setMobileList);
  }

  function fetchMoreArticles(limit, fn) {
    if (!fetchMore) return;

    fetchMore({
      variables: { limit, offset: list.length },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return;

        if (innerKey)
          fetchMoreResult[key][innerKey] = [
            ...prev[key][innerKey],
            ...fetchMoreResult[key][innerKey]
          ];

        if (fn)
          fn(
            innerKey
              ? fetchMoreResult[key][innerKey]
              : [...prev[key], ...fetchMoreResult[key]]
          );

        return {
          [key]: innerKey
            ? fetchMoreResult[key]
            : [...prev[key], ...fetchMoreResult[key]]
        };
      }
    });
  }
}

function firstItemSrc(imageUrl) {
  const width = window.innerWidth > 0 ? window.innerWidth : screen.width;

  if (width < 450) return src(imageUrl, 405, 180);
  if (width < 850) return src(imageUrl, 765, 180);

  return src(imageUrl, 391, 220);
}
